import React from 'react'
import { createRoot } from 'react-dom/client'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SelectSectionsModal from '../components/select-sections-modal'
import WarningModal from '../components/warning-modal'
import SetupCompleteModal from '../components/setup-complete-modal'
import Sidebar from '../components/project-sidebar'
import ViewReflections from '../components/view-reflections'
import Queue from '../components/project-queue'
import LiveQuestionList from '../components/live-question-list'
import PulseModal from '../components/pulse-modal'
import { ApolloProvider, useQuery, useMutation } from '@apollo/client'
import client from '../client'
import findProject from '../queries/find-project'
import renameProject from '../mutations/rename-project'
import addSections from '../mutations/add-sections'
import debounce from 'debounce'

const countStudents = (sections) => {
  return (
    sections &&
    sections.map((s) => s.students.length).reduce((i, acc) => i + acc, 0)
  )
}

function ProjectForm(props) {
  const [name, setName] = React.useState(props.name)
  const [selectSectionsOpen, setSelectSectionsOpen] = React.useState(false)
  const [setupComplete, setSetupComplete] = React.useState(false)
  const [viewReflectionsOpen, setViewReflectionsOpen] = React.useState(false)
  const [viewReflectionsStudent, setViewReflectionsStudent] =
    React.useState(null)
  const [warningOpen, setWarningOpen] = React.useState(false)
  const [activeAssignment, setActiveAssignment] = React.useState(null)
  const [pulseAssignment, setPulseAssignment] = React.useState(null)
  const [renameProjectMutation] = useMutation(renameProject)
  const [addSectionsMutation] = useMutation(addSections)
  const { error, loading, data, refetch } = useQuery(findProject, {
    variables: { id: props.id },
  })

  if (loading) return <div>Loading</div>
  if (error) return <div>Error</div>

  const renameThisProject = debounce((name) => {
    const { id } = props

    renameProjectMutation({
      variables: { id, name },
    }).then(() => {})
  }, 500)

  const handleNameFocus = () => {
    if (name === 'Untitled Journal') {
      setName('')
    }
  }

  const handleNameChange = (e) => {
    setName(e.target.value)

    if (e.target.value.length > 0) {
      renameThisProject(e.target.value)
    }
  }

  const handleAddSections = (sectionIds) => {
    const { id } = props
    setSelectSectionsOpen(false)
    addSectionsMutation({
      variables: { id, sectionIds },
    }).then(() => {
      // This might need changing
      refetch()
    })
  }

  const handleCancelAssignment = () => {
    setActiveAssignment(null)
    refetch()
  }

  const handleSetupComplete = () => {
    setSetupComplete(true)
    setActiveAssignment(null)
    refetch()
  }

  const handleSetupCompleteClose = () => {
    setSetupComplete(false)
  }

  const setThisActiveAssignment = (assignment) => {
    if (name && name.length > 0 && data && data.project.sections.length > 0) {
      setActiveAssignment(assignment)
    } else {
      toggleWarning()
    }
  }

  const toggleWarning = (e) => {
    e && e.preventDefault()
    setWarningOpen(!warningOpen)
  }

  const toggleViewReflections = (e) => {
    e && e.preventDefault()
    setViewReflectionsOpen(!viewReflectionsOpen)
  }

  const setThisViewReflectionsStudent = (student) => {
    if (student) {
      setViewReflectionsOpen(true)
      setViewReflectionsStudent(student)
    } else {
      setViewReflectionsStudent(student)
    }
  }

  const toggleSelectSections = () => {
    setSelectSectionsOpen(!selectSectionsOpen)
  }

  const togglePulse = (assignment) => {
    setPulseAssignment(assignment)
  }

  const selectSectionLinkText = (project) => {
    if (project && project.sections.length) {
      const firstSection = project.sections[0]
      const groupName = firstSection.group.name
      const sectionNames = project.sections.map((s) =>
        s.name.replace(/\s/g, '\u00A0')
      )
      return `${groupName}: ${sectionNames.join(', ')}`
    } else {
      return 'Add Groups'
    }
  }

  const viewReflectionsEnabled = () => {
    return (
      data &&
      data.project &&
      data.project.assignments &&
      data.project.assignments.filter(
        (a) => a.submittedAnswers && a.submittedAnswers.length > 0
      ).length !== 0
    )
  }

  const projectTitle = (currentUserRole, name) => {
    if (currentUserRole === 'teacher' || currentUserRole === 'collaborator') {
      return (
        <div className="field floating project-name">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            onFocus={handleNameFocus}
            required
          />
          <div className="highlight" />
        </div>
      )
    } else {
      return (
        <div className="project-name">
          <div className="title">{name}</div>
        </div>
      )
    }
  }

  const sectionDisplay = (currentUserRole, project) => {
    if (currentUserRole === 'teacher' || currentUserRole === 'collaborator') {
      return (
        <a onClick={toggleSelectSections}>
          <i className="material-icons">add_circle</i>
          {selectSectionLinkText(project)}
        </a>
      )
    } else {
      return (
        <div className="sections-list">{selectSectionLinkText(project)}</div>
      )
    }
  }
  const { id } = props

  const selectedSections = data && data.project && data.project.sections
  const lastDueAt = data.project && data.project.lastDueAt
  const liveQuestions = data.project && data.project.liveQuestions
  const sections = data.project && data.project.sections
  const currentUserRole = data.project && data.project.currentUserRole
  const numStudents = countStudents(sections)
  const pulseAssignmentId = pulseAssignment && pulseAssignment.id
  const isMeta = (a) => a.metaReflection
  const liveMeta = liveQuestions && liveQuestions.filter(isMeta)
  const metaWasAssigned = liveMeta && liveMeta.length > 0

  return (
    <div className="project-form">
      <Sidebar
        data={data}
        lastDueAt={lastDueAt}
        assignment={activeAssignment}
        onCancel={handleCancelAssignment}
        onMetaSave={handleSetupComplete}
      />
      <form>
        <div className="header">
          {projectTitle(currentUserRole, name)}
          <button
            disabled={!viewReflectionsEnabled()}
            onClick={toggleViewReflections}
          >
            View Journals
          </button>
        </div>
        <div className="subheader">
          {sectionDisplay(currentUserRole, data.project)}
          <SelectSectionsModal
            id={id}
            projectId={id}
            isOpen={selectSectionsOpen}
            onCancel={toggleSelectSections}
            onSave={handleAddSections}
            selected={selectedSections}
            appElement={document.getElementById('project-form')}
          />
        </div>
        <div className="project-queue">
          <LiveQuestionList
            questions={liveQuestions}
            numStudents={numStudents}
            togglePulse={togglePulse}
            onAssign={setThisActiveAssignment}
          />
          <div>
            <hr />
            <div className="queue">
              <h2>Queue</h2>
              <p>The Queue is your workspace for organizing questions.</p>
              {!metaWasAssigned && (
                <Queue
                  projectId={id}
                  activeAssignment={activeAssignment}
                  onAssign={setThisActiveAssignment}
                  allowAssign={!metaWasAssigned}
                />
              )}
            </div>
            <div className="meta-reflection">
              <p>
                <b>Meta-Reflection</b>
              </p>
              <Queue
                projectId={id}
                metaReflection
                onAssign={setThisActiveAssignment}
                allowAssign={true}
                activeAssignment={activeAssignment}
              />
            </div>
          </div>
        </div>
      </form>
      <ViewReflections
        isOpen={viewReflectionsOpen}
        onClose={toggleViewReflections}
        project={data.project}
        selectedStudent={viewReflectionsStudent}
        setSelectedStudent={setThisViewReflectionsStudent}
      />
      <PulseModal
        id={pulseAssignmentId}
        onClose={togglePulse}
        setViewReflectionsStudent={setThisViewReflectionsStudent}
      />
      <WarningModal
        isOpen={warningOpen}
        onClose={toggleWarning}
        appElement={document.getElementById('project-form')}
      >
        <h2>Additional Project Setup Required</h2>
        <p style={{ marginTop: 48, marginBottom: 54 }}>
          Your project needs a Name and Group(s) before you can assign
          questions.
        </p>
      </WarningModal>
      <SetupCompleteModal
        isOpen={setupComplete}
        onCancel={handleSetupCompleteClose}
        appElement={document.getElementById('project-form')}
      />
    </div>
  )
}

const projectForm = document.getElementById('project-form')
if (projectForm) {
  const id = projectForm.getAttribute('data-id')
  const name = projectForm.getAttribute('data-name')
  const props = { id, name }
  const root = createRoot(projectForm)

  root.render(
    <ApolloProvider client={client}>
      <DndProvider backend={HTML5Backend}>
        <ProjectForm {...props} />
      </DndProvider>
    </ApolloProvider>
  )
}
